import type { AreaComp, Comp, GameObj } from "kaplay";
import { config } from "../config";
import { PX_PER_M } from "../config/constants";
import { emit } from "../core/events";
import { k } from "../core/kaplay";
import { dropLaunchVz, isPop, rampLaunchVz } from "../systems/air";
import type { Ramp } from "./ramp";
import type { Rider } from "./rider";

export interface TakeoffComp extends Comp {
    rampAhead(): boolean;
}

const air = config.tricks.air;
const MAX_APPROACH_DEG = 60;

function launchOff(r: Rider, o: Ramp, pop: boolean) {
    const { speed } = r.motion;
    const { dropM = 0, launchScale = 1 } = o.rampConfig;
    const vz = dropM > 0 ? dropLaunchVz(speed, pop, air) : rampLaunchVz(speed, launchScale, pop, air);
    o.spent = true;
    r.launch(vz, dropM);
    emit("jump", { source: dropM > 0 ? "drop" : "ramp", strength: vz, pop });
}

function landedOnTable(r: Rider, o: Ramp): boolean {
    const tablePx = o.rampConfig.tablePx ?? 0;
    return r.pos.y < o.lipY() + tablePx;
}

export function takeoff(): TakeoffComp {
    let lastJumpPress = -Infinity;
    let launchedFrom: Ramp | null = null;

    return {
        id: "takeoff",
        require: ["rider", "area"],

        add(this: Rider & GameObj<AreaComp>) {
            this.onCollideUpdate("ramp", (other) => {
                const o = other as Ramp;
                if (o.spent || this.state !== "riding" || this.pos.y < o.lipY()) return;
                if (Math.abs(this.motion.heading) > MAX_APPROACH_DEG) return;
                launchOff(this, o, isPop(k.time() - lastJumpPress, air));
                launchedFrom = o;
            });

            this.onStateEnter("riding", (landed?: unknown) => {
                if (landed && launchedFrom && landedOnTable(this, launchedFrom)) {
                    this.motion = { ...this.motion, speed: this.motion.speed * (1 - air.tableShortLoss) };
                }
                launchedFrom = null;
            });
        },

        update(this: Rider) {
            if (this.intent.jump) lastJumpPress = k.time();
        },

        rampAhead(this: Rider) {
            const reach = this.motion.speed * air.popWindowS * PX_PER_M;
            return k.get("ramp").some((other) => {
                const o = other as Ramp;
                const [w, h] = o.rampConfig.hitboxPx ?? [0, 0];
                const dy = o.lipY() - this.pos.y;
                return !o.spent && Math.abs(o.pos.x - this.pos.x) < w / 2 && dy > 0 && dy < h + reach;
            });
        },
    };
}
